/**
 * The clock maps score beats to performance milliseconds.
 *
 * A clock always runs: it is how timing deviations and held lengths are measured. A set
 * tempo is a constant clock. A clock that follows the player's tempo plugs in through the
 * same interface, so nothing downstream changes when the pace source changes.
 */

import { GradingConfigError } from './types';

export interface Clock {
  /** The performance time (ms) at which a score position (beats) falls due. */
  msAtBeat(beat: number): number;
  /** Milliseconds per beat around a score position: the local tempo, for tempo-relative units. */
  msPerBeatAt(beat: number): number;
}

const MS_PER_MINUTE = 60000;

/** A steady clock at one tempo, with beat 0 falling due at `startMs`. */
export function constantTempoClock(bpm: number, startMs = 0): Clock {
  if (!(bpm > 0) || !Number.isFinite(bpm)) {
    throw new GradingConfigError(`tempo must be a positive number of beats per minute, got ${bpm}`);
  }
  const msPerBeat = MS_PER_MINUTE / bpm;
  return {
    msAtBeat: (beat: number) => startMs + beat * msPerBeat,
    msPerBeatAt: () => msPerBeat,
  };
}

export interface ReferenceTempoSources {
  /** Tempo the preset sets, if any. */
  readonly presetTempoBpm?: number;
  /** Tempo the session is set to, if any. */
  readonly sessionTempoBpm?: number;
  /** The score's notated tempo, if any. */
  readonly notatedTempoBpm?: number;
}

/**
 * The tempo that turns notated lengths into time when no clock drives matching (order
 * matching). It is always a set tempo - the preset's, else the session's, else the score's
 * notated tempo - and never one inferred from the player, so a replay always judges the same.
 * Returns null when no source is set.
 */
export function resolveReferenceTempo(sources: ReferenceTempoSources): number | null {
  const candidates = [sources.presetTempoBpm, sources.sessionTempoBpm, sources.notatedTempoBpm];
  const found = candidates.find((bpm) => typeof bpm === 'number' && bpm > 0);
  return found === undefined ? null : found;
}
